import { useEffect, useState } from "react";
import { Icon } from "../lib/Icon";
import { Avatar, Stars } from "../lib/components";
import { useAuth } from "../contexts/AuthContext";
import { fetchMyStats, type AlbumStats } from "../api/inventory";
import { EditProfileModal } from "./EditProfileModal";
import type { Route } from "../App";

interface Props {
  go: (r: Route) => void;
}

export function ProfileScreen(_props: Props) {
  const { user } = useAuth();
  const [stats, setStats] = useState<AlbumStats | null>(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    fetchMyStats().then(setStats).catch(() => null);
  }, []);

  if (!user) return null;

  const badges: { name: string; icon: string; color: string; earned: boolean }[] = [
    { name: "Primer trueque", icon: "swap", color: "var(--primary)", earned: user.trades_completed >= 1 },
    { name: "10 intercambios", icon: "flame", color: "var(--accent-magenta)", earned: user.trades_completed >= 10 },
    { name: "5 estrellas", icon: "star", color: "var(--accent-amber)", earned: user.stars_avg >= 4.8 },
    { name: "Verificado", icon: "check", color: "var(--accent-cobalt)", earned: user.verified },
    { name: "Coleccionista", icon: "trophy", color: "var(--accent-amber)", earned: (stats?.owned ?? 0) >= 100 },
    { name: "Generoso", icon: "gift", color: "var(--primary)", earned: false },
  ];

  return (
    <div className="page">
      <div className="profile-card">
        <div className="profile-banner" />
        <div className="profile-body">
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
            <Avatar user={user} size="xl" />
            <div style={{ paddingBottom: 8, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <h2 style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>{user.name}</h2>
                {user.verified && (
                  <div className="chip primary"><Icon name="check" size={11} /> Verificado</div>
                )}
                <div className="chip amber"><Icon name="trophy" size={11} /> Nivel {user.level}</div>
                {(user.role === "admin" || user.role === "super_admin") && (
                  <div className="chip cobalt"><Icon name="settings" size={11} /> Nilabs</div>
                )}
              </div>
              <div className="tiny muted" style={{ marginTop: 4 }}>
                {user.handle || user.email} · {user.city || "Sin ciudad"}
                {user.region && ` · ${user.region}`}
                {user.country && user.country !== "Colombia" && ` · ${user.country}`}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <Stars value={user.stars_avg} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{Number(user.stars_avg || 0).toFixed(1)}</span>
                <span className="tiny muted">({user.trades_completed} intercambios)</span>
              </div>
              {user.bio && <p style={{ marginTop: 8, color: "var(--fg-muted)", fontSize: 14 }}>{user.bio}</p>}
            </div>
            <button className="btn btn-secondary" onClick={() => setShowEdit(true)}>
              <Icon name="settings" size={16} /> Editar perfil
            </button>
          </div>

          {stats && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 24 }}>
              {[
                { label: "Pegadas", value: stats.owned },
                { label: "Faltantes", value: stats.missing },
                { label: "Repetidas", value: stats.dups },
                { label: "Trueques", value: user.trades_completed },
              ].map((s) => (
                <div key={s.label} className="stat" style={{ padding: 12, background: "var(--bg-subtle)", borderRadius: 10, alignItems: "center" }}>
                  <div className="stat-value" style={{ fontSize: 28 }}>{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="card">
          <div className="card-header"><div className="card-title">Insignias</div></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {badges.map((b) => (
              <div key={b.name} style={{ textAlign: "center", padding: 12, background: "var(--bg-subtle)", borderRadius: 10, opacity: b.earned ? 1 : 0.4 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: b.color, color: "white", display: "grid", placeItems: "center", margin: "0 auto 8px" }}>
                  <Icon name={b.icon} size={20} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 600 }}>{b.name}</div>
              </div>
            ))}
          </div>
          <div className="tiny muted" style={{ marginTop: 12, textAlign: "center" }}>
            Las insignias se desbloquean cuando completas intercambios reales.
          </div>
        </div>

        <div className="card">
          <div className="card-header"><div className="card-title">Reseñas</div></div>
          <div style={{ textAlign: "center", padding: "24px 12px", color: "var(--fg-muted)" }}>
            <Icon name="star" size={28} />
            <div style={{ marginTop: 8, fontSize: 13 }}>
              Aún no tienes reseñas. Las recibirás después de completar tus primeros intercambios.
            </div>
          </div>
        </div>
      </div>

      {showEdit && <EditProfileModal onClose={() => setShowEdit(false)} />}
    </div>
  );
}
